<template>
  <div class="rdv-time-field">
    <div
      class="rdv-time"
      :class="{
        'rdv-time--focus': active !== null,
        'rdv-time--invalid': !!error,
        'rdv-time--empty': h === null && active === null,
      }"
      role="group"
      aria-label="Heure"
      @mousedown="onBoxMousedown"
    >
      <v-icon size="18" class="rdv-time__icon">mdi-clock-outline</v-icon>
      <input
        ref="h"
        class="rdv-time__seg"
        :class="{ 'rdv-time__seg--active': active === 'h' }"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        spellcheck="false"
        placeholder="--"
        aria-label="Heures"
        :value="segText('h')"
        @focus="onFocus('h')"
        @blur="onBlur"
        @keydown="onKeydown('h', $event)"
        @input="onInput('h', $event)"
        @compositionend="onCompositionEnd('h', $event)"
        @paste.prevent="onPaste('h', $event)"
      />
      <span class="rdv-time__sep">:</span>
      <input
        ref="m"
        class="rdv-time__seg"
        :class="{ 'rdv-time__seg--active': active === 'm' }"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        spellcheck="false"
        placeholder="--"
        aria-label="Minutes"
        :value="segText('m')"
        @focus="onFocus('m')"
        @blur="onBlur"
        @keydown="onKeydown('m', $event)"
        @input="onInput('m', $event)"
        @compositionend="onCompositionEnd('m', $event)"
        @paste.prevent="onPaste('m', $event)"
      />
    </div>
    <div v-if="error" class="rdv-time__error">
      <v-icon size="14">mdi-alert-circle-outline</v-icon>
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script>
// ─────────────────────────────────────────────────────────────────────────
// Champ heure « HH : MM » (remplace le DatePicker en mode heure).
//   • chiffres uniquement : les lettres sont ignorées, clavier numérique sur mobile ;
//   • passage automatique des heures aux minutes : 1430 → 14:30, 930 → 09:30,
//     « 9h30 » fonctionne aussi ; on peut aussi cliquer directement sur les minutes ;
//   • ↑ / ↓ : ±1 h ou ±5 min · ← / → : changer de partie · Retour arrière : effacer ;
//   • v-model au même format que le DatePicker : { hours, minutes, seconds }.
// ─────────────────────────────────────────────────────────────────────────
const pad = (n) => (n < 10 ? "0" : "") + n;

export default {
  name: "TimeField",
  props: {
    modelValue: { type: Object, default: undefined },
    // Message affiché sous le champ (bordure rouge), ex. fin avant le début.
    error: { type: String, default: "" },
    minuteStep: { type: Number, default: 5 },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      h: null,
      m: null,
      active: null, // partie en cours d'édition : "h" | "m" | null
      buffer: "", // 1er chiffre tapé, en attente du 2e
      cleared: false, // partie vidée avec Retour arrière (affiche "--")
      pending: false, // saisie commencée mais pas encore envoyée au parent
      keepBuffer: false,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler() {
        let r = this.readModel();
        this.h = r.h;
        this.m = r.m;
        this.pending = false;
        // Valeur hors limites (ex. 10:60 ou 24:00) : on renvoie la valeur corrigée.
        if (r.fixed) this.emitValue();
      },
    },
  },
  methods: {
    readModel() {
      let v = this.modelValue;
      let h = v ? parseInt(v.hours, 10) : NaN;
      let m = v ? parseInt(v.minutes, 10) : NaN;
      if (!Number.isFinite(h)) return { h: null, m: null, fixed: false };
      let fixed = !Number.isFinite(m);
      if (fixed) m = 0;
      if (m > 59) {
        h += Math.floor(m / 60);
        m = m % 60;
        fixed = true;
      }
      if (m < 0 || h < 0) {
        m = Math.max(m, 0);
        h = Math.max(h, 0);
        fixed = true;
      }
      if (h > 23) {
        h = 23;
        m = 59;
        fixed = true;
      }
      return { h, m, fixed };
    },
    emitValue() {
      this.pending = false;
      this.$emit("update:modelValue", { hours: this.h, minutes: this.m, seconds: 0 });
    },
    segText(seg) {
      if (this.active === seg) {
        if (this.buffer) return this.buffer;
        if (this.cleared) return "";
      }
      let v = seg === "h" ? this.h : this.m;
      return v === null ? "" : pad(v);
    },
    // Le texte affiché reste maîtrisé (une lettre tapée disparaît aussitôt).
    syncInputs() {
      if (this.$refs.h) this.$refs.h.value = this.segText("h");
      if (this.$refs.m) this.$refs.m.value = this.segText("m");
    },
    goTo(seg, buffer = "") {
      if (this.pending) this.emitValue();
      this.active = seg;
      this.buffer = buffer;
      this.cleared = false;
      if (buffer) this.pending = true;
      let el = this.$refs[seg];
      if (el && document.activeElement !== el) {
        this.keepBuffer = true;
        el.focus();
        this.keepBuffer = false;
      }
      this.syncInputs();
    },
    onBoxMousedown(e) {
      if (e.target === this.$refs.h || e.target === this.$refs.m) return;
      e.preventDefault();
      this.goTo(this.active || "h");
    },
    onFocus(seg) {
      if (this.active !== seg && !this.keepBuffer) {
        if (this.pending) this.emitValue();
        this.buffer = "";
        this.cleared = false;
      }
      this.active = seg;
    },
    onBlur(e) {
      let to = e.relatedTarget;
      if (this.keepBuffer || to === this.$refs.h || to === this.$refs.m) return;
      if (this.pending) this.emitValue();
      this.active = null;
      this.buffer = "";
      this.cleared = false;
      this.syncInputs();
    },
    onKeydown(seg, e) {
      let k = e.key;
      if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      if (k === "ArrowUp" || k === "ArrowDown") {
        e.preventDefault();
        this.step(seg, k === "ArrowUp" ? 1 : -1);
      } else if (k === "ArrowRight" && seg === "h") {
        e.preventDefault();
        this.goTo("m");
      } else if (k === "ArrowLeft" && seg === "m") {
        e.preventDefault();
        this.goTo("h");
      } else if (k === "Backspace" || k === "Delete") {
        e.preventDefault();
        this.backspace(seg);
      } else if (seg === "h" && [":", "h", "H", ".", ",", " "].indexOf(k) !== -1) {
        e.preventDefault();
        this.goTo("m");
      } else if (k && k.length === 1 && (k < "0" || k > "9")) {
        e.preventDefault(); // lettres et symboles : rien n'est saisi
      }
    },
    onInput(seg, e) {
      if (e.isComposing) return; // saisie IME : traitée à la fin (compositionend)
      let type = e.inputType || "";
      if (type.indexOf("delete") === 0) this.backspace(seg);
      else if (e.data) this.typeDigits(seg, String(e.data).replace(/\D/g, ""));
      this.syncInputs();
    },
    onCompositionEnd(seg, e) {
      this.typeDigits(seg, String(e.data || "").replace(/\D/g, ""));
      this.syncInputs();
    },
    onPaste(seg, e) {
      let txt = e.clipboardData ? e.clipboardData.getData("text") : "";
      this.typeDigits(seg, String(txt || "").replace(/\D/g, "").slice(0, 4));
      this.syncInputs();
    },
    typeDigits(seg, digits) {
      if (!digits) return;
      let st = { seg, buffer: this.active === seg ? this.buffer : "", h: this.h, m: this.m };
      for (let i = 0; i < digits.length; i++) this.applyDigit(st, digits[i]);
      this.h = st.h === null ? 0 : st.h;
      this.m = st.m === null ? 0 : st.m;
      this.cleared = false;
      if (st.seg !== seg) {
        this.pending = false;
        this.goTo(st.seg, st.buffer); // le focus passe aux minutes
      } else {
        this.buffer = st.buffer;
      }
      // On n'envoie la valeur qu'une fois la partie complète (pas d'erreur
      // « fin avant le début » qui clignote pendant la frappe).
      if (st.buffer === "") this.emitValue();
      else this.pending = true;
    },
    applyDigit(st, d) {
      let n = Number(d);
      if (st.seg === "h") {
        if (st.buffer === "") {
          st.h = n;
          if (n > 2) st.seg = "m"; // 3 à 9 → 03 à 09, on passe aux minutes
          else st.buffer = d; // 0, 1 ou 2 : on attend le 2e chiffre
        } else {
          let v = Number(st.buffer + d);
          st.buffer = "";
          st.seg = "m";
          if (v <= 23) st.h = v;
          else this.applyDigit(st, d); // « 25 » → 02 h puis 5 dans les minutes
        }
      } else if (st.buffer === "") {
        st.m = n;
        if (n <= 5) st.buffer = d; // 6 à 9 → 06 à 09 (terminé)
      } else {
        st.m = Number(st.buffer + d);
        st.buffer = "";
      }
    },
    backspace(seg) {
      if (this.buffer || !this.cleared) {
        if (this.pending) {
          // saisie non validée : on revient à la dernière valeur connue
          let r = this.readModel();
          this.h = r.h;
          this.m = r.m;
          this.pending = false;
        }
        this.buffer = "";
        this.cleared = true; // partie affichée vide, prête pour une nouvelle saisie
      } else if (seg === "m") {
        this.goTo("h");
        this.cleared = true;
      }
      this.syncInputs();
    },
    step(seg, dir) {
      let h = this.h === null ? 0 : this.h;
      let m = this.m === null ? 0 : this.m;
      if (seg === "h") {
        h = (h + dir + 24) % 24;
      } else {
        let s = this.minuteStep > 0 ? this.minuteStep : 5;
        m = dir > 0 ? (Math.floor(m / s) + 1) * s : (Math.ceil(m / s) - 1) * s;
        m = ((m % 60) + 60) % 60;
      }
      this.h = h;
      this.m = m;
      this.buffer = "";
      this.cleared = false;
      this.emitValue();
      this.syncInputs();
    },
  },
};
</script>

<style scoped>
.rdv-time-field {
  width: 100%;
  text-align: left;
  text-transform: none;
  font-weight: 400;
  letter-spacing: normal;
}
.rdv-time {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  min-height: 46px;
  padding: 4px 10px;
  background: #fff;
  border: 2px solid #cfd8dc;
  border-radius: 10px;
  cursor: text;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rdv-time--empty {
  border-color: #e57373;
}
.rdv-time--focus {
  border-color: #5c6bc0;
  box-shadow: 0 0 0 3px rgba(92, 107, 192, 0.16);
}
.rdv-time--invalid {
  border-color: #e53935;
}
.rdv-time--invalid.rdv-time--focus {
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.16);
}
.rdv-time__icon {
  margin-right: 6px;
  color: #90a4ae;
}
.rdv-time--focus .rdv-time__icon {
  color: #5c6bc0;
}
.rdv-time__seg {
  box-sizing: content-box;
  width: 2ch;
  padding: 2px 6px;
  border: 0;
  border-radius: 6px;
  outline: none;
  background: transparent;
  color: #263238;
  font: inherit;
  font-size: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  text-align: center;
  caret-color: transparent;
  cursor: pointer;
  transition: background-color 0.12s, color 0.12s;
}
.rdv-time__seg:hover {
  background: #eceff7;
}
.rdv-time__seg::placeholder {
  color: #b0bec5;
  opacity: 1;
}
.rdv-time__seg::selection {
  background: transparent;
}
.rdv-time__seg--active,
.rdv-time__seg--active:hover {
  background: #5c6bc0;
  color: #fff;
}
.rdv-time__seg--active::placeholder {
  color: rgba(255, 255, 255, 0.75);
}
.rdv-time__sep {
  padding: 0 1px;
  color: #90a4ae;
  font-size: 20px;
  font-weight: 600;
  user-select: none;
}
.rdv-time__error {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: #e53935;
  font-size: 12px;
  line-height: 1.3;
}
@media (max-width: 380px) {
  .rdv-time__icon {
    display: none;
  }
}
</style>
